const LOCAL_API_BASE = String(process.env.REACT_APP_LOCAL_API_BASE || "http://localhost:8000").replace(/\/+$/, "");
const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const DEFAULT_FETCH_TIMEOUT_MS = Math.max(
  5000,
  Number(process.env.REACT_APP_API_TIMEOUT_MS || 25000) || 25000
);
const AUTH_TOKEN_KEY = "fb_auth_token";
const AUTH_EXPIRED_EVENT = "app:auth-expired";

function createTraceId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function isLocalDevHost() {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function resolveApiBase() {
  if (API_BASE) {
    return API_BASE;
  }

  if (isLocalDevHost()) {
    return LOCAL_API_BASE;
  }

  return API_BASE;
}

function buildApiUrl(path, baseUrl = resolveApiBase()) {
  return baseUrl ? `${baseUrl}${path}` : path;
}

export function getAuthToken() {
  return window.localStorage.getItem(AUTH_TOKEN_KEY) || "";
}

export function setAuthToken(token) {
  if (!token) return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken() {
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const normalized = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const decoded = window.atob(normalized);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export function getAuthTokenClaims() {
  return decodeJwtPayload(getAuthToken());
}

export function getAuthTokenExpiryMs() {
  const payload = getAuthTokenClaims();
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return 0;
  return exp * 1000;
}

function notifyAuthExpired() {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

export function getAuthExpiredEventName() {
  return AUTH_EXPIRED_EVENT;
}

function withAuthHeaders(headers = {}) {
  const token = getAuthToken();
  if (!token) return headers;
  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
}

function withTraceHeaders(headers = {}, method = "GET") {
  if (String(method).toUpperCase() === "GET") {
    return headers;
  }

  if (headers["X-Trace-Id"] || headers["x-trace-id"]) {
    return headers;
  }

  return {
    ...headers,
    "X-Trace-Id": createTraceId(),
  };
}

async function fetchOnce(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const primaryUrl = buildApiUrl(path);
  const timeoutMs =
    Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : DEFAULT_FETCH_TIMEOUT_MS;
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => {
    timeoutController.abort(new Error(`Request timed out after ${timeoutMs}ms`));
  }, timeoutMs);

  let signal = timeoutController.signal;
  if (options.signal) {
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.any === "function") {
      signal = AbortSignal.any([options.signal, timeoutController.signal]);
    } else {
      options.signal.addEventListener(
        "abort",
        () => {
          timeoutController.abort(options.signal.reason || new Error("Request aborted"));
        },
        { once: true }
      );
    }
  }

  const primaryOptions = {
    ...options,
    method,
    headers: withTraceHeaders(withAuthHeaders(options.headers || {}), method),
    signal,
  };

  delete primaryOptions.timeoutMs;

  try {
    return await fetch(primaryUrl, primaryOptions);
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error(`Request timed out while calling ${path}`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function refreshAccessTokenIfPossible() {
  const token = getAuthToken();
  if (!token) return false;

  const response = await fetchOnce("/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    return false;
  }

  const data = await response.json();
  const nextToken = data?.access_token || "";
  if (!nextToken) {
    return false;
  }

  setAuthToken(nextToken);
  return true;
}

export async function authFetch(path, options = {}) {
  const shouldHandleUnauthorized = !String(path || "").startsWith("/auth/login") && !String(path || "").startsWith("/auth/refresh");

  const handleUnauthorized = async (response, retryOptions) => {
    if (!shouldHandleUnauthorized) return;
    if (response.status !== 401) return;

    if (await refreshAccessTokenIfPossible()) {
      const retryResponse = await fetchOnce(path, retryOptions);
      if (retryResponse.status !== 401) {
        return retryResponse;
      }
    }

    if (!getAuthToken()) return response;
    clearAuthToken();
    notifyAuthExpired();
    return response;
  };

  const response = await fetchOnce(path, options);
  const retriedResponse = await handleUnauthorized(response, options);
  return retriedResponse || response;
}

export async function login(username, password) {
  const res = await authFetch("/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || "Login failed");
  }

  const data = await res.json();
  const token = data?.access_token || "";
  if (!token) {
    throw new Error("Login response missing access token");
  }

  setAuthToken(token);
  return data;
}

export async function refreshSession() {
  const res = await fetchOnce("/auth/refresh", {
    method: "POST",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body?.detail || "Session refresh failed");
    error.status = res.status;
    throw error;
  }

  const data = await res.json();
  const token = data?.access_token || "";
  if (!token) {
    throw new Error("Refresh response missing access token");
  }

  setAuthToken(token);
  return data;
}

export async function fetchMe() {
  const res = await authFetch("/auth/me", {
    method: "GET",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body?.detail || "Session validation failed");
    error.status = res.status;
    throw error;
  }

  return res.json();
}

export async function beginFreshbooksConnect(includeTeams = false) {
  const query = includeTeams ? "?include_teams=true" : "";
  const res = await authFetch(`/oauth/connect-url${query}`, {
    method: "GET",
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.detail || "Failed to start FreshBooks connect");
  }

  const url = body?.authorize_url;
  if (!url) {
    throw new Error("Missing FreshBooks authorize URL");
  }

  window.location.assign(url);
}

export async function fetchIcalLink() {
  const res = await authFetch("/calendar/ical-link", {
    method: "GET",
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.detail || "Failed to load iCal link");
  }

  return body;
}

export async function rotateIcalLink() {
  const res = await authFetch("/calendar/ical-link/rotate", {
    method: "POST",
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.detail || "Failed to rotate iCal link");
  }

  return body;
}
